"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./auth-provider";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { updatePlayerProfile } from "@/services/player/update-player-profile";
import { listItemDecks } from "@/services/item-deck/list-item-decks";
import { getPlayerQueueStatus } from "@/services/duel/get-player-queue-status";
import { getCountryByLanguageCode } from "@/data/countries";
import { ItemDeckPokemon, PlayerProfileOutput } from "types/web-app";
import { useFingerprint } from "@/hooks/use-fingerprint";

type UserState =
  | { status: "idle" | "loading" }
  | { status: "loaded"; profile: PlayerProfileOutput }
  | { status: "not_registered" }
  | { status: "onboarding_in_progress" }
  | { status: "error"; error: string };

type UserContextValue = {
  state: UserState;
  reloadProfile: () => Promise<void>;
  updateProfile: (updates: { country?: string }) => Promise<void>;
  pokeCoins: number;
  setPokeCoins: (coins: number | ((prev: number) => number)) => void;
  pokeShards: number | null;
  setPokeShards: (
    shards: number | null | ((prev: number | null) => number | null),
  ) => void;
  rewardedDuelsToday: number;
  playerCountry: string | null | undefined;
  playerElo: number | null;
  playerWins: number | null;
  playerLosses: number | null;
  username: string | undefined;
  playerId: string | undefined;
  mainDeckId: number | undefined;
  mainDeckPokemons: ItemDeckPokemon[];
  duelStatus: {
    status: "IDLE" | "WAITING" | "MATCHED" | "IN_DUEL";
    duelId: string | null;
  };
};

const UserContext = createContext<UserContextValue | null>(null);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getAccessToken, state: authState } = useAuth();
  const [userState, setUserState] = useState<UserState>({ status: "idle" });
  const [pokeCoins, setPokeCoins] = useState<number>(0);
  const [pokeShards, setPokeShards] = useState<number | null>(null);
  const [rewardedDuelsToday, setRewardedDuelsToday] = useState<number>(0);
  const [playerCountry, setPlayerCountry] = useState<string | null | undefined>(
    undefined,
  );
  const [mainDeckId, setMainDeckId] = useState<number | undefined>(undefined);
  const [mainDeckPokemons, setMainDeckPokemons] = useState<ItemDeckPokemon[]>(
    [],
  );
  const [duelStatus, setDuelStatus] = useState<UserContextValue["duelStatus"]>({
    status: "IDLE",
    duelId: null,
  });

  /**
   * Fetches player profile and handles country auto-detection
   */
  const fetchPlayerProfile = async () => {
    if (import.meta.env.DEV) {
      const { isOfflineMode } = await import("@/dev/offline-mode");
      if (isOfflineMode()) {
        const { buildOfflineDecks, buildOfflineProfile } = await import("@/dev/offline-build");
        const { getSession } = await import("@/dev/offline-session");
        const { player, deckIds } = getSession();
        const profile = buildOfflineProfile(player);
        const decks = buildOfflineDecks(player, deckIds).decks;
        const mainDeck = decks.find((deck) => deck.is_main) || decks[0];

        setPokeCoins(profile.poke_coins);
        setPokeShards(profile.poke_shards);
        setRewardedDuelsToday(profile.stats.rewarded_duels_today ?? 0);
        setPlayerCountry(profile.country);
        setMainDeckId(mainDeck?.id);
        setMainDeckPokemons(mainDeck?.pokemons ?? []);
        setDuelStatus({ status: "IDLE", duelId: null });
        setUserState({ status: "loaded", profile });
        return;
      }
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      setPokeCoins(0);
      setPokeShards(0);
      setRewardedDuelsToday(0);
      setUserState({ status: "error", error: "Not authenticated" });
      return;
    }

    try {
      // Only show loading state if we don't already have a loaded profile
      // This prevents UI flicker during background token refreshes
      if (userState.status !== "loaded") {
        setUserState({ status: "loading" });
      }

      const profileResponse = await getPlayerProfile();

      // Handle not registered (assuming 404 or !ok with specific message)
      if (!profileResponse.ok) {
        if (
          profileResponse.statusCode === 404 ||
          profileResponse.message?.includes("not found") ||
          profileResponse.message?.includes("not registered")
        ) {
          setUserState({ status: "not_registered" });
          return;
        }
        throw new Error(profileResponse.message || "Failed to fetch profile");
      }

      const profile = profileResponse.data;

      if (!profile) {
        setUserState({ status: "not_registered" });
        return;
      }

      if (!profile.onboarding_completed) {
        setUserState({ status: "onboarding_in_progress" });
        return;
      }

      // Update state with profile data
      setPokeCoins(profile.poke_coins ?? 0);
      setPokeShards(profile.poke_shards ?? 0);
      setRewardedDuelsToday(profile.stats?.rewarded_duels_today ?? 0);
      setPlayerCountry(profile.country);

      // Fetch all necessary data for the context in parallel
      try {
        const [decksRes, queueRes] = await Promise.allSettled([
          listItemDecks(),
          getPlayerQueueStatus(),
        ]);

        if (decksRes.status === "fulfilled" && decksRes.value.ok) {
          const decks = decksRes.value.data.decks;
          const mainDeck = decks.find((deck) => deck.is_main) || decks[0];
          if (mainDeck) {
            setMainDeckId(mainDeck.id);
            setMainDeckPokemons(mainDeck.pokemons ?? []);
          } else {
            setMainDeckId(undefined);
            setMainDeckPokemons([]);
          }
        }

        if (queueRes.status === "fulfilled" && queueRes.value.ok) {
          setDuelStatus({
            status: queueRes.value.data.status,
            duelId: queueRes.value.data.queue_entry?.duel_id ?? null,
          });
        }
      } catch (error) {
        console.error(
          "Failed to fetch additional data during profile load:",
          error,
        );
      }

      setUserState({ status: "loaded", profile });

      // Auto-detect country if not set
      if (!profile.country) {
        await autoDetectCountry(profile);
      }
    } catch (error) {
      console.error("Failed to fetch player profile:", error);
      setPokeCoins(0);
      setPokeShards(0);
      setRewardedDuelsToday(0);
      setUserState({
        status: "error",
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  /**
   * Auto-detects country from the browser language.
   */
  const autoDetectCountry = async (profile: PlayerProfileOutput) => {
    try {
      const detectedCountry = getCountryByLanguageCode(navigator.language);

      if (detectedCountry) {
        // Auto-update profile
        try {
          const updateRes = await updatePlayerProfile({
            country: detectedCountry,
          });
          if (updateRes.ok && updateRes.data?.data?.country) {
            setPlayerCountry(updateRes.data.data.country);
            // Update the profile in state as well
            if (userState.status === "loaded") {
              setUserState({
                status: "loaded",
                profile: {
                  ...userState.profile,
                  country: updateRes.data.data.country,
                },
              });
            }
          }
        } catch (e) {
          console.error("Failed to auto-update country:", e);
        }
      }
    } catch (error) {
      console.error("Failed to auto-detect country:", error);
    }
  };

  /**
   * Updates player profile and refreshes local state
   */
  const updateProfile = async (updates: { country?: string }) => {
    if (!getAccessToken()) {
      throw new Error("Not authenticated");
    }

    try {
      const updateRes = await updatePlayerProfile(updates);
      if (updateRes.ok && updateRes.data?.data) {
        const updatedProfile = updateRes.data.data;
        setPokeCoins(updatedProfile.poke_coins ?? 0);
        setPokeShards(updatedProfile.poke_shards ?? 0);
        setPlayerCountry(updatedProfile.country);
        setUserState({ status: "loaded", profile: updatedProfile });
      } else {
        throw new Error(updateRes.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      throw error;
    }
  };

  // Fetch profile when auth state changes to authenticated
  useEffect(() => {
    if (authState.status === "authenticated") {
      fetchPlayerProfile();
    } else if (authState.status === "idle" || authState.status === "loading") {
      setUserState({ status: "idle" });
    } else {
      setUserState({ status: "error", error: "Not authenticated" });
    }
  }, [authState.status]);

  const { playerId, playerElo, playerWins, playerLosses, username } = useMemo(() => {
    if (userState.status === "loaded") {
      const fallbackPlayerId =
        (userState.profile as any)?.id ||
        (userState.profile as any)?.playerId ||
        (authState.status === "authenticated"
          ? authState.user.player_id
          : undefined);
      return {
        playerId: userState.profile.player_id || fallbackPlayerId,
        playerElo: userState.profile.stats?.elo ?? null,
        playerWins: userState.profile.stats?.wins ?? null,
        playerLosses: userState.profile.stats?.losses ?? null,
        username: userState.profile.username,
      };
    }

    return {
      playerId: undefined,
      playerElo: null,
      playerWins: null,
      playerLosses: null,
      username: undefined,
    };
  }, [userState, authState]);

  const value = useMemo<UserContextValue>(
    () => ({
      state: userState,
      reloadProfile: fetchPlayerProfile,
      updateProfile,
      pokeCoins,
      setPokeCoins,
      pokeShards,
      setPokeShards,
      rewardedDuelsToday,
      playerCountry,
      mainDeckId,
      mainDeckPokemons,
      duelStatus,
      playerId,
      playerElo,
      playerWins,
      playerLosses,
      username,
    }),
    [
      userState,
      pokeCoins,
      pokeShards,
      rewardedDuelsToday,
      playerCountry,
      mainDeckId,
      mainDeckPokemons,
      duelStatus,
      authState.status,
      authState.status === "authenticated"
        ? authState.user.username
        : undefined,
    ],
  );

  return (
    <UserContext.Provider value={value}>
      <FingerprintCollector />
      {children}
    </UserContext.Provider>
  );
};

const FingerprintCollector = () => {
  useFingerprint();
  return null;
};

/**
 * Hook to access user state and methods.
 */
export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within <UserProvider>");
  return ctx;
}
